import React from "react";
import { createRoot } from "react-dom/client";
import App from "./App";
import { ThemeProvider } from "./contexts/ThemeContext";
import { BrowserRouter } from "react-router-dom";
import { ConfirmDialogProvider } from "react-mui-confirm";
import axios from "axios";
import { AuthProvider } from "./components/auth/AuthContext";
import GlobalProvider from "./contexts/GlobalContext";

const container = document.getElementById("root");
const root = createRoot(container);

const AUTH_SKIP_REFRESH_URLS = ["/api/login", "/api/refresh", "/api/logout", "/api/register"];

let refreshInFlight = null;

const clearLocalSession = () => {
  sessionStorage.clear();
};

const refreshAccessToken = () => {
  if (refreshInFlight) {
    return refreshInFlight;
  }

  refreshInFlight = (async () => {
    const refreshToken = sessionStorage.getItem("refreshToken");
    if (!refreshToken) {
      clearLocalSession();
      throw new Error("No refresh token");
    }

    const response = await fetch("/api/refresh", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        refreshToken,
      }),
    });

    if (!response.ok) {
      clearLocalSession();
      throw new Error("Session expired");
    }

    const responseData = await response.json();
    const accessTokenNew = responseData.accessToken;
    const refreshTokenNew = responseData.refreshToken;
    const expiresInNew = responseData.expiresIn;

    if (!accessTokenNew || !refreshTokenNew || !expiresInNew) {
      clearLocalSession();
      throw new Error("Invalid refresh response");
    }

    const newExpiryTime = Date.now() + expiresInNew * 1000;
    sessionStorage.setItem("accessToken", accessTokenNew);
    sessionStorage.setItem("refreshToken", refreshTokenNew);
    sessionStorage.setItem("accessTokenExpiry", newExpiryTime.toString());

    return accessTokenNew;
  })().finally(() => {
    refreshInFlight = null;
  });

  return refreshInFlight;
};

axios.interceptors.request.use(
  async (config) => {
    const requestUrl = config.url || "";
    const skipRefresh = AUTH_SKIP_REFRESH_URLS.some((path) => requestUrl.includes(path));

    if (skipRefresh) {
      return config;
    }

    let accessToken = sessionStorage.getItem("accessToken");
    const accessTokenExpiry = sessionStorage.getItem("accessTokenExpiry");
    const now = Date.now();
    const isAccessTokenExpired = accessTokenExpiry ? now >= parseInt(accessTokenExpiry, 10) : true;

    if (isAccessTokenExpired) {
      try {
        accessToken = await refreshAccessToken();
      } catch (error) {
        return Promise.reject(error);
      }
    }

    if (accessToken) {
      config.headers["Authorization"] = `Bearer ${accessToken}`;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

root.render(
  <BrowserRouter>
    <ThemeProvider>
      <ConfirmDialogProvider>
        <AuthProvider>
          <GlobalProvider>
            <App />
          </GlobalProvider>
        </AuthProvider>
      </ConfirmDialogProvider>
    </ThemeProvider>
  </BrowserRouter>
);
